/**
 * 应用顶部导航栏
 */

import { useEffect, useState } from 'react'
import { Layout, Space, Badge, Dropdown, Avatar, Typography, Button } from 'antd'
import { useNavigate } from 'react-router-dom'
import {
  BellOutlined,
  UserOutlined,
  WifiOutlined,
  DisconnectOutlined,
  SettingOutlined,
  LogoutOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import {
  AlarmRecord,
  useDeviceStore,
  useUIStore,
  useUserStore,
} from '@/store/useDeviceStore'
import AlarmPanel from '@/components/AlarmPanel/AlarmPanel'
import { get } from '@/utils/request'

const { Header } = Layout
const { Text } = Typography

function AppHeader() {
  const navigate = useNavigate()
  const { wsConnected } = useDeviceStore()
  const { alarms, clearAlarms, setAlarms } = useUIStore()
  const { username, role, logout, token } = useUserStore()
  const [alarmPanelVisible, setAlarmPanelVisible] = useState(false)

  useEffect(() => {
    if (!token) {
      clearAlarms()
      return
    }

    const loadUnHandledAlarms = async () => {
      try {
        const data = await get<{ items: AlarmRecord[] }>('/api/v1/alarms/unhandled', {
          showError: false,
        })
        setAlarms(data.items)
      } catch (error) {
        console.error('加载未处理报警失败:', error)
      }
    }

    loadUnHandledAlarms()
  }, [token, clearAlarms, setAlarms])

  const unreadAlarmCount = alarms.filter((a) => !a.handled).length

  // 处理登出
  const handleLogout = () => {
    clearAlarms()
    logout()
    navigate('/login')
  }

  // 用户下拉菜单
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      label: (
        <Space>
          <UserOutlined />
          <span>{username || '未登录'}</span>
          {role === 'admin' && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              (管理员)
            </Text>
          )}
        </Space>
      ),
    },
    { type: 'divider' },
    {
      key: 'settings',
      label: (
        <Space>
          <SettingOutlined />
          <span>系统设置</span>
        </Space>
      ),
      onClick: () => navigate('/settings'),
    },
    {
      key: 'logout',
      label: (
        <Space>
          <LogoutOutlined />
          <span>退出登录</span>
        </Space>
      ),
      onClick: handleLogout,
    },
  ]

  return (
    <>
      <Header>
        {/* Logo 和标题 */}
        <Space>
          <img
            src="/monitor.svg"
            alt="logo"
            style={{ width: 32, height: 32, position: 'relative', left: -6, top: 10 }}
          />
          <Text strong style={{ color: '#fff', fontSize: 18 }}>
            低空监测系统
          </Text>
        </Space>

        {/* 右侧工具栏 */}
        <Space size="large">
          {/* WebSocket 连接状态 */}
          <Space>
            {wsConnected ? (
              <WifiOutlined style={{ color: '#52c41a', fontSize: 18 }} />
            ) : (
              <DisconnectOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
            )}
            <Text style={{ color: wsConnected ? '#52c41a' : '#ff4d4f' }}>
              {wsConnected ? '已连接' : '未连接'}
            </Text>
          </Space>

          {/* 报警通知 */}
          <Badge count={unreadAlarmCount} overflowCount={99}>
            <Button
              type="text"
              icon={<BellOutlined style={{ fontSize: 18, color: '#fff' }} />}
              onClick={() => setAlarmPanelVisible(true)}
            />
          </Badge>

          {/* 用户菜单 */}
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }}>
              <Avatar icon={<UserOutlined />} />
              <Text style={{ color: '#fff' }}>{username || '未登录'}</Text>
            </Space>
          </Dropdown>
        </Space>
      </Header>

      {/* 报警面板 */}
      <AlarmPanel
        visible={alarmPanelVisible}
        onClose={() => setAlarmPanelVisible(false)}
      />
    </>
  )
}

export default AppHeader
